import { useEffect, useRef } from 'react';
import useMountEffect from './useMountEffect';

// Module-level registry of the single currently-open "floating" popover (star
// note, label upsell, …). These are antd controlled popovers opened with
// `trigger={[]}`, which never close on outside interaction — so without this,
// opening a second one leaves the first open. Whenever one opens it tells the
// previously open one to close, guaranteeing at most one is open app-wide.
let activeInstance: { id: object; close: () => void } | null = null;

/**
 * Makes a controlled popover mutually exclusive with every other popover using
 * this hook. Pass the current `open` flag and a `close` callback; when this
 * popover opens, any other open one is closed.
 */
export default function useExclusivePopover(open: boolean, close: () => void): void {
	// Stable identity for this instance (the object reference never changes).
	const idRef = useRef<object>({});
	// Always call the latest close so we don't capture a stale closure.
	const closeRef = useRef(close);
	closeRef.current = close;

	useEffect(() => {
		if (open) {
			if (activeInstance && activeInstance.id !== idRef.current) {
				activeInstance.close();
			}
			activeInstance = { id: idRef.current, close: () => closeRef.current() };
		} else if (activeInstance && activeInstance.id === idRef.current) {
			activeInstance = null;
		}
	}, [open]);

	// Release the slot if this popover unmounts while open.
	useMountEffect(() => () => {
		if (activeInstance && activeInstance.id === idRef.current) {
			activeInstance = null;
		}
	});
}
